import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import CarDomain from './domains/CarDomain';
import SigefDomain from './domains/SigefDomain';
import SncrDomain from './domains/SncrDomain';
import CafirDomain from './domains/CafirDomain';
import CcirDomain from './domains/CcirDomain';
import CndDomain from './domains/CndDomain';
import EntidadesDomain from './domains/EntidadesDomain';
import LegalCivilDomain from './domains/LegalCivilDomain';
import LegalCriminalDomain from './domains/LegalCriminalDomain';
import SobreposicaoDomain from './domains/SobreposicaoDomain';
import IbamaDomain from './domains/IbamaDomain';
import { propertyApi } from '../../services/BaseApiService.jsx';
import { ReportConfigProvider } from '../../contexts/ReportConfigContext.jsx';
import { ReportLayoutProvider } from '../../contexts/ReportLayoutContext.jsx';
import ExecutiveDashboard from './ExecutiveDashboard';
import ExecutiveDashboardMap from './ExecutiveDashboardMap';
import GeoespacialTab from './GeoespacialTab';
import ReportPrintLayout from './ReportPrintLayout';
import { generateAllReportMapSnapshots } from '../../utils/reportMaps/reportMapSnapshots';

export default function ReportPage() {
  const [fileId, setFileId] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [docs, setDocs] = useState(null);
  const [docsError, setDocsError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [detailsTab, setDetailsTab] = useState(0);
  const [mapSnapshots, setMapSnapshots] = useState({});
  const [printing, setPrinting] = useState(false);

  const handleLoad = async () => {
    const trimmed = fileId.trim();
    if (!trimmed) {
      setError('Informe um file_id para carregar o relatório.');
      return;
    }

    setLoading(true);
    setError('');
    setSnapshot(null);
    setDocs(null);
    setDocsError('');

    try {
      const response = await propertyApi.get(`/api/v1/state/${encodeURIComponent(trimmed)}`, {
        headers: {
          Accept: 'application/json',
        },
      });

      const data = response?.data;

      // Em quase todos os cenários o backend retorna { snapshot, geometryData, docs }
      // Mas, por segurança, se não houver snapshot, exibimos o JSON inteiro retornado.
      const snap = data && typeof data === 'object' && 'snapshot' in data ? data.snapshot : data;
      setSnapshot(snap);

      // Carregar documentos em seguida, usando o mesmo file_id
      try {
        const docsResponse = await propertyApi.get(`/api/v1/docs/${encodeURIComponent(trimmed)}`, {
          headers: {
            Accept: 'application/json',
          },
        });

        const docsData = docsResponse?.data;
        setDocs(docsData);
      } catch (docsErr) {
        console.error('Erro ao carregar documentos do relatório:', docsErr);
        setDocsError('Erro ao carregar documentos para este relatório.');
      }
    } catch (e) {
      console.error('Erro ao carregar estado do relatório:', e);
      setError('Erro ao carregar o relatório. Verifique o file_id e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const docsFiles = Array.isArray(docs?.files) ? docs.files : [];
  const docsFileIdRaw = (docs && docs.file_id) || snapshot?._metadata?.file_id || fileId;
  const docsFileId =
    typeof docsFileIdRaw === 'string'
      ? docsFileIdRaw.replace(/\.json$/i, '').trim()
      : docsFileIdRaw;

  const fetchDocBlob = async (name) => {
    if (!docsFileId || !name) return null;
    const encodedFileId = encodeURIComponent(String(docsFileId));
    const encodedPath = String(name)
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    const url = `/api/v1/docs/${encodedFileId}/${encodedPath}`;
    const resp = await propertyApi.get(url, { responseType: 'blob' });
    const contentType = resp?.headers?.['content-type'] || 'application/pdf';
    return new Blob([resp.data], { type: contentType });
  };

  const handleOpenDoc = async (name) => {
    try {
      const blob = await fetchDocBlob(name);
      if (!blob) return;
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      console.error('Erro ao abrir documento do relatório:', e);
      setDocsError('Erro ao abrir documento para este relatório.');
    }
  };

  const handleDownloadDoc = async (name) => {
    try {
      const blob = await fetchDocBlob(name);
      if (!blob) return;
      const blobUrl = URL.createObjectURL(blob);
      const fileLabel = String(name || '');
      const fileNameOnly = fileLabel.split('/').pop() || fileLabel;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileNameOnly;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Erro ao baixar documento do relatório:', e);
      setDocsError('Erro ao baixar documento para este relatório.');
    }
  };

  const handleMapSnapshot = (id, dataUrl) => {
    if (!id || !dataUrl) return;
    setMapSnapshots((prev) => {
      if (prev[id] === dataUrl) return prev;
      return { ...prev, [id]: dataUrl };
    });
  };

  const handlePrint = async () => {
    if (!snapshot) {
      setError('Carregue o relatório antes de imprimir ou salvar em PDF.');
      return;
    }

    setError('');
    setPrinting(true);

    try {
      const snapshots = await generateAllReportMapSnapshots(snapshot);
      setMapSnapshots(snapshots || {});

      // pequeno atraso para garantir re-render do layout de impressão com os novos mapas
      setTimeout(() => {
        window.print();
      }, 500);
    } catch (e) {
      console.error('Erro ao preparar mapas para impressão:', e);
      setError('Erro ao preparar mapas para impressão. Tente novamente.');
      window.print();
    } finally {
      setPrinting(false);
    }
  };

  const legalArray = snapshot?.properties?.legal;
  let legalCivilData = null;
  let legalCriminalData = null;

  if (Array.isArray(legalArray) && legalArray.length > 0) {
    const legal0 = legalArray[0] || {};

    const resumoProcessos = Array.isArray(legal0.situacao_juridica?.processos)
      ? legal0.situacao_juridica.processos
      : [];
    const civilResumo = resumoProcessos.filter(
      (p) => typeof p?.tipo === 'string' && p.tipo.toLowerCase() === 'civil',
    );
    const criminalResumo = resumoProcessos.filter(
      (p) => typeof p?.tipo === 'string' && p.tipo.toLowerCase() === 'criminal',
    );

    const detalhesProcessos = Array.isArray(legal0.civilstatus?.processos)
      ? legal0.civilstatus.processos
      : [];
    const civilDetalhes = detalhesProcessos.filter(
      (p) => typeof p?.tipo_processo === 'string' && p.tipo_processo.toUpperCase() === 'CIVIL',
    );
    const criminalDetalhes = detalhesProcessos.filter(
      (p) => typeof p?.tipo_processo === 'string' && p.tipo_processo.toUpperCase() === 'CRIMINAL',
    );

    if (civilResumo.length || civilDetalhes.length) {
      legalCivilData = {
        situacao_juridica: { processos: civilResumo },
        civilstatus: { ...(legal0.civilstatus || {}), processos: civilDetalhes },
      };
    }

    if (criminalResumo.length || criminalDetalhes.length) {
      legalCriminalData = {
        situacao_juridica: { processos: criminalResumo },
        civilstatus: { ...(legal0.civilstatus || {}), processos: criminalDetalhes },
      };
    }
  }

  return (
    <ReportConfigProvider>
      <ReportLayoutProvider>
        <Box sx={{ p: 3 }} className="report-screen-root">
          <Typography variant="h5" sx={{ mb: 2 }}>
            Report - visão básica
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label="file_id"
              size="small"
              value={fileId}
              onChange={(e) => setFileId(e.target.value)}
            />
            <Button variant="contained" onClick={handleLoad} disabled={loading}>
              {loading ? 'Carregando...' : 'Carregar relatório'}
            </Button>
            <Button
              variant="outlined"
              onClick={handlePrint}
              disabled={loading || !snapshot || printing}
            >
              {printing ? 'Preparando mapas...' : 'Imprimir / Salvar PDF'}
            </Button>
            {loading && <CircularProgress size={20} />}
          </Box>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {snapshot && (
            <Box sx={{ mt: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{
                  mb: 1.5,
                  minHeight: 36,
                }}
              >
                <Tab
                  label="Dashboard"
                  sx={{
                    minHeight: 36,
                    py: 0,
                    px: 1.5,
                    fontSize: '0.8rem',
                  }}
                />
                <Tab
                  label="Detalhes"
                  sx={{
                    minHeight: 36,
                    py: 0,
                    px: 1.5,
                    fontSize: '0.8rem',
                  }}
                />
                <Tab
                  label="Geoespacial"
                  sx={{
                    minHeight: 36,
                    py: 0,
                    px: 1.5,
                    fontSize: '0.8rem',
                  }}
                />
                <Tab
                  label="Documentos"
                  sx={{
                    minHeight: 36,
                    py: 0,
                    px: 1.5,
                    fontSize: '0.8rem',
                  }}
                />
              </Tabs>

              <Divider sx={{ mb: 2 }} />

              {activeTab === 0 && (
                <Box
                  id="report-dashboard"
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    alignItems: 'stretch',
                  }}
                >
                  <Box
                    data-html2canvas-ignore="true"
                    sx={{
                      flexBasis: { xs: '100%', md: '40%' },
                      flexGrow: 0,
                      height: { xs: 280, md: '78vh' },
                      maxHeight: { md: '78vh' },
                      minHeight: 240,
                    }}
                  >
                    <ExecutiveDashboardMap
                      snapshot={snapshot}
                      onSnapshotReady={handleMapSnapshot}
                      mapId="dashboard-main"
                    />
                  </Box>

                  <Box
                    sx={{
                      flexBasis: { xs: '100%', md: '60%' },
                      flexGrow: 0,
                      maxWidth: { md: '60%' },
                      display: 'flex',
                      flexDirection: 'column',
                      height: { md: '78vh' },
                      maxHeight: { md: '78vh' },
                      overflowY: { md: 'auto' },
                    }}
                  >
                    <ExecutiveDashboard snapshot={snapshot} docsFiles={docsFiles} />
                  </Box>
                </Box>
              )}

              {activeTab === 1 && (
                <Box id="report-details">
                  <Tabs
                    value={detailsTab}
                    onChange={(_, newValue) => setDetailsTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2 }}
                  >
                    <Tab label="CAR" />
                    <Tab label="SIGEF" />
                    <Tab label="SNCR" />
                    <Tab label="CAFIR" />
                    <Tab label="CCIR" />
                    <Tab label="CND" />
                    <Tab label="Entidades" />
                    <Tab label="Jurídico - Cível" />
                    <Tab label="Jurídico - Criminal" />
                    <Tab label="Sobreposição" />
                    <Tab label="IBAMA" />
                    <Tab label="Snapshot bruto" />
                  </Tabs>

                  <Divider sx={{ mb: 2 }} />

                  {detailsTab === 0 && (
                    <CarDomain
                      data={snapshot?.properties?.car}
                      sobreposicao={snapshot?.properties?.sobreposicao}
                    />
                  )}

                  {detailsTab === 1 && <SigefDomain data={snapshot?.properties?.sigef} />}

                  {detailsTab === 2 && <SncrDomain data={snapshot?.properties?.sncr} />}

                  {detailsTab === 3 && <CafirDomain data={snapshot?.properties?.cafir} />}

                  {detailsTab === 4 && <CcirDomain data={snapshot?.properties?.serpro_ccir} />}

                  {detailsTab === 5 && <CndDomain data={snapshot?.properties?.serpro_cnd} />}

                  {detailsTab === 6 && <EntidadesDomain data={snapshot?.properties?.entidades} />}

                  {detailsTab === 7 && <LegalCivilDomain data={legalCivilData} />}

                  {detailsTab === 8 && <LegalCriminalDomain data={legalCriminalData} />}

                  {detailsTab === 9 && (
                    <SobreposicaoDomain data={snapshot?.properties?.sobreposicao} />
                  )}

                  {detailsTab === 10 && (
                    <IbamaDomain
                      debitos={snapshot?.ibama_debitos}
                      embargos={snapshot?.ibama_embargos}
                      regularidade={snapshot?.ibama_regularidade}
                    />
                  )}

                  {detailsTab === 11 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Snapshot bruto (completo)
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          maxHeight: '50vh',
                          overflow: 'auto',
                          p: 2,
                          bgcolor: '#f7fafc',
                          borderRadius: 1,
                          fontSize: 12,
                        }}
                      >
                        {JSON.stringify(snapshot, null, 2)}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {activeTab === 2 && (
                <Box id="report-geospatial">
                  <GeoespacialTab snapshot={snapshot} />
                </Box>
              )}

              {activeTab === 3 && (
                <Box id="report-documents">
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Documentos
                  </Typography>
                  {docsError && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      {docsError}
                    </Typography>
                  )}

                  {!docsError && loading && (
                    <Typography variant="body2">Carregando documentos...</Typography>
                  )}

                  {!docsError && !loading && docs && docsFiles.length === 0 && (
                    <Typography variant="body2">
                      Nenhum documento encontrado para este relatório.
                    </Typography>
                  )}

                  {!docsError && !loading && docs && docsFiles.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {docsFiles.length === 1
                          ? '1 documento disponível para este relatório.'
                          : `${docsFiles.length} documentos disponíveis para este relatório.`}
                      </Typography>
                      <Box
                        sx={{
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          overflow: 'hidden',
                        }}
                      >
                        {docsFiles.map((name, index) => {
                          const fileLabel = String(name || '');

                          return (
                            <Box
                              key={`${fileLabel}-${index}`}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1.5,
                                py: 1,
                                borderTop: index === 0 ? 'none' : '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ mr: 2, flex: 1, wordBreak: 'break-all' }}
                              >
                                {fileLabel}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleOpenDoc(fileLabel)}
                                >
                                  Abrir
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleDownloadDoc(fileLabel)}
                                >
                                  Download
                                </Button>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {snapshot && <Box sx={{ mt: 2 }}>{/* conteúdo principal já renderizado acima */}</Box>}
        </Box>

        {/* Layout dedicado para impressão: visível apenas em @media print */}
        {snapshot && (
          <Box className="report-print-root" sx={{ display: 'none' }}>
            <ReportPrintLayout
              snapshot={snapshot}
              userName={snapshot?._metadata?.requested_by || ''}
              docsFiles={docsFiles}
              mapSnapshots={mapSnapshots}
            />
          </Box>
        )}
      </ReportLayoutProvider>
    </ReportConfigProvider>
  );
}
